import {Component, createRef} from 'react';
import throttle from 'lodash.throttle';
import Link from '@components/Link';
import Post from '@components/Post';
import Scroller from '@components/Scroller';
import type {PostStrip as PostStripType} from '@interfaces/PostStrip';
import {getRefElement} from '@utils/refUtils';

const THROTTLE_INTERVAL = 200;
const POST_IMAGE_WIDTH_TO_HEIGHT_MODIFIER = 0.66;
const SCROLL_BUTTON_HEIGHT_MODIFIER = 0.5;
const ITEM_MARGIN = 24;

interface State {
    itemWidth: number;
    buttonVerticalPosition: number;
}

export default class PostStrip extends Component<PostStripType, State> {
    public readonly state: State = {
        itemWidth: 300,
        buttonVerticalPosition: 100,
    };

    private readonly containerRef = createRef<HTMLDivElement>();

    private readonly handleResize = throttle(() => {
        this.calculateItemWidth();
        this.calculateButtonVerticalPosition();
    }, THROTTLE_INTERVAL);

    public componentDidMount() {
        this.calculateItemWidth();
        this.calculateButtonVerticalPosition();
        window.addEventListener('resize', this.handleResize);
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    private calculateItemWidth() {
        try {
            const container = getRefElement(this.containerRef);
            const rawWidth = (container.querySelector('.post-strip__item')! as HTMLDivElement)
                .offsetWidth;

            const itemWidth = rawWidth + ITEM_MARGIN;
            this.setState({itemWidth});
        } catch (refError) {
            console.error(refError);
        }
    }

    private calculateButtonVerticalPosition() {
        try {
            /*
             * we might not be able to accurately calculate the position based on the image height,
             * as it will not be loaded yet on component mount, so let's use image width and our knowledge
             * that the image aspect ratio should be 3x2
             */
            const container = getRefElement(this.containerRef);
            const imageWidth = (container.querySelector('.post__img')! as HTMLDivElement)
                .offsetWidth;
            this.setState({
                buttonVerticalPosition: Math.floor(
                    imageWidth *
                        POST_IMAGE_WIDTH_TO_HEIGHT_MODIFIER *
                        SCROLL_BUTTON_HEIGHT_MODIFIER,
                ),
            });
        } catch (refError) {
            console.error(refError);
        }
    }

    public render() {
        const buttonProps = {
            style: {
                top: this.state.buttonVerticalPosition,
            },
            dataSet: {
                stripName: this.props.title,
            },
        };

        return (
            <div className="post-strip" data-vr-zone={`Колбаса "${this.props.title || ''}"`}>
                <div className="post-strip__title">
                    {this.props.link ? (
                        <Link
                            to={this.props.link}
                            className="post-strip__link js-track-strip-title-click"
                        >
                            {this.props.title}
                        </Link>
                    ) : null}
                    {!this.props.link && this.props.title}
                </div>
                <Scroller
                    containerRef={this.containerRef}
                    itemWidth={this.state.itemWidth}
                    className="post-strip__body"
                    buttonProps={buttonProps}
                    leftButtonProps={{
                        className: 'js-track-strip-left-click',
                    }}
                    rightButtonProps={{
                        className: 'js-track-strip-right-click',
                    }}
                >
                    {this.props.posts.map((item, key: number) => (
                        <Post
                            key={item.id}
                            postData={item}
                            className="post-strip__item js-track-strip-item-click"
                            forcedSmall
                            dataSet={{
                                num: key + 1,
                            }}
                            ioName={`Блок ${key + 1}`}
                        />
                    ))}
                </Scroller>
            </div>
        );
    }
}
